import React, { ReactElement, useState } from "react";
import { IconType } from "react-icons";
import { AiOutlineFileText } from "react-icons/ai";
import {
    FaFilePowerpoint, FaRegFileAudio, FaRegFileCode, FaRegFileImage,
} from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import { GoFile, GoFileCode, GoFileZip } from "react-icons/go";
import {
    GrDocumentPdf, GrDocumentWord, GrDocumentZip, GrFormClose,
} from "react-icons/gr";
import { RiFileExcel2Fill } from "react-icons/ri";
import Confirm from "../confirm/Confirm";
import { LoadingIcon } from "../loading/Loading";
import Styles from "./Attachment.module.scss";

const IMAGE_MIME_TYPES = new Set([
    "image/tiff",
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/apng",
    "image/bmp",
    "image/x-icon",
    "image/svg+xml",
    "image/webp",
]);

const VIDEO_MIME_TYPE = new Set([
    "application/vnd.apple.mpegurl",
    "application/x-mpegurl",
    "video/3gpp",
    "video/mp4",
    "video/mpeg",
    "video/ogg",
    "video/quicktime",
    "video/webm",
    "video/x-m4v",
    "video/ms-asf",
    "video/x-ms-wmv",
    "video/x-msvideo",
]);

const MIME_TO_IMAGE: Record<string, IconType> = {
    "application/pdf": GrDocumentPdf,
    "application/zip": GrDocumentZip,
    "application/msword": GrDocumentWord,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": GrDocumentWord,
    "application/gzip": GoFileZip,
    "application/x-tar": GoFileZip,
    "text/html": GoFile,
    "text/plain": AiOutlineFileText,
    "application/json": GoFileCode,
    "application/vnd.ms-powerpoint": FaFilePowerpoint,
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": FaFilePowerpoint,
    "application/vnd.ms-excel": RiFileExcel2Fill,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": RiFileExcel2Fill,
    "text/csv": RiFileExcel2Fill,
    "application/xml": FaRegFileCode,
    "text/xml": FaRegFileCode,

};

const AUDIO_ICON = FaRegFileAudio;

const DEFAULT_ICON = FaRegFileImage;

export interface AttachmentProps {
    mimeType?: string;
    title?: string;
    isUploading?: boolean;
    url: string;
    size?: "sm" | "lg";
    onClose?: () => void
    className?: string;
}

function removeMiddleString(content: string | undefined, length: number): string | undefined {
    return (!content || content.length <= length)
        ? content
        : (`${content.substr(0, length / 2)}...${content.substr(content.length - (length / 2))}`);
}

const ATTACHMENT_CONTEXT = "attachments";
export default function Attachment({
    mimeType, title, url, size, onClose, className, isUploading,
}: AttachmentProps): ReactElement {
    const [isFullScreen, setFullScreen] = useState(false);

    if (isUploading) {
        return (
            <div
                className={`${className} `}
            >
                <LoadingIcon className={Styles["loading-icon-sm"]} />
            </div>
        );
    }
    if (IMAGE_MIME_TYPES.has(mimeType as string)) {
        const makeFullscreen = (event: React.MouseEvent) => {
            event.preventDefault();
            setFullScreen(true);
        };
        return (
            <>

                <Confirm
                    size="xl"
                    shouldShow={isFullScreen}
                    closeText="Close"
                    onHide={() => setFullScreen(false)}
                    dialogClassName="full-size-container"
                    keyboardContext={ATTACHMENT_CONTEXT}
                >
                    <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                        <img src={url} alt={title as string} className={`${Styles["dont-oversize"]} m-4 d-block`} />
                    </div>
                </Confirm>

                <div
                    className={`border rounded  ${size === "sm" ? "d-flex align-items-center m-0 p-0" : " m-0 p-0 col-6"}
                                ${className}`}
                >
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none"
                        onClick={makeFullscreen}
                        title={title}
                    >
                        <img src={url} alt={title as string} className={size === "sm" ? Styles["size-sm"] : "card-img-top"} />
                    </a>
                    {onClose && (
                        <button type="button" onClick={onClose} className="btn btn-none">
                            <GrFormClose />
                            {" "}
                        </button>
                    )}
                    {size !== "sm" && (
                        <div
                            className="px-2 d-flex justify-content-between"
                        >
                            {title && (
                                <div className="text-light" title={title}>
                                    {removeMiddleString(title, 40)}
                                </div>
                            )}

                            <a
                                className="text-decoration-none text-center text-center d-block"
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                title={title}
                            >
                                <FiDownload />
                            </a>

                        </div>
                    )}

                </div>

            </>
        );
    } if (VIDEO_MIME_TYPE.has(mimeType as string)) {
        return (

            <div
                className={`${size === "sm" ? Styles["size-sm"] : "flex-grow-0 col-11 card"} ${className} `}
            >
                <video controls width="100%" className={size === "sm" ? "" : "flex-grow-1 flex-shrink-1"}>
                    <source
                        src={url}
                        type="video/mp4"
                    />
                    <track kind="captions" />
                    Sorry, your browser doesn&apos;t support embedded videos.
                </video>
                {onClose && (
                    <button type="button" onClick={onClose} className="btn btn-none">
                        <GrFormClose />
                        {" "}
                    </button>
                )}
                {size !== "sm" && (
                    <div
                        className="px-2 d-flex justify-content-between"
                    >
                        {title && (
                            <div className="text-light">
                                {title}
                            </div>
                        )}

                        <a
                            className="text-decoration-none text-center text-center d-block"
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            title={title}
                        >
                            <FiDownload />
                        </a>

                    </div>
                )}

            </div>

        );
    }
    const Icon = (mimeType?.startsWith("audio/") ? AUDIO_ICON : MIME_TO_IMAGE[mimeType ?? ""]) ?? DEFAULT_ICON;
    return (
        <a
            className={`border rounded d-flex align-items-center ${
                size === "sm" ? "d-flex p-1" : "p-3 col-6 flex-grow-1 "
            } text-decoration-none  text-center ${className}`}
            href={url}
            target="_blank"
            rel="noreferrer"
        >
            <Icon className="fs-1 p-2" />
            <span>{removeMiddleString(title, size === "sm" ? 20 : 40) || "[Attachment]"}</span>
            {onClose && (
                <button type="button" onClick={onClose} className="btn btn-none">
                    <GrFormClose />
                    {" "}
                </button>
            )}
        </a>
    );
}
Attachment.defaultProps = {
    mimeType: "",
    title: "",
    size: "lg",
    className: "",
    isUploading: undefined,
    onClose: undefined,
};
